// LabComplyx Live Demo — complete workflow data model (6 workflows × 6 steps)
// field cls: 'hl' = highlight (teal), 'warn' = amber, 'err' = red

export const TAG_DEFS = {
  ai: { label: "🤖 AI Assisted", color: "teal" },
  pending: { label: "⏳ Pending", color: "amber" },
  review: { label: "🔍 In Review", color: "blue" },
  compliant: { label: "✅ Compliant", color: "teal" },
  signed: { label: "✍️ Signed", color: "purple" },
};

export const WORKFLOWS = [
  {
    id: "capa",
    icon: "🔄",
    name: "CAPA Documentation",
    desc: "Corrective & Preventive Action — deviation to closure",
    regs: ["CLIA D5791", "CAP GEN.54100", "ISO 8.7"],
    docId: "CAPA-2026-0047",
    timeSaved: "4.2 hrs",
    completionScore: "4.2 hrs saved",
    completionMsg:
      "CAPA lifecycle ran itself — detection, RCA, and plan arrived pre-drafted at the human QA gate. Full audit trail, regulatory mapping, and e-signature proof captured as the work happened. Inspection-ready, with 4.2 hours saved as a byproduct.",
    steps: [
      {
        label: "Detection", icon: "🔍", reg: "CLIA D5791",
        desc: "AI agent scans instrument logs, QC results, and EHR flags to auto-detect out-of-range events. Deviation intake form pre-populated.",
        agent: "AI Agent scanning QC logs… detected 3σ exceedance on Analyzer ID LH-22",
        gate: false, tags: ["ai", "pending"],
        section: "DEVIATION INTAKE",
        fields: [
          { k: "Document ID", v: "CAPA-2026-0047", c: "hl" },
          { k: "Trigger Source", v: "AI Deviation Detection — Automated Scan" },
          { k: "Instrument ID", v: "LH-22 (CBC Analyzer)" },
          { k: "Exceedance", v: "WBC Delta > 3σ (Lot: LOT-2026-0089)", c: "warn" },
          { k: "CLIA Reference", v: "D5791 — QC Procedures", c: "hl" },
          { k: "CAP Reference", v: "GEN.54100 — Corrective Action", c: "hl" },
          { k: "Status", v: "INTAKE COMPLETE — Awaiting RCA" },
          { k: "AI Confidence", v: "94.7%" },
        ],
        guardrails: [
          { icon: "🔍", name: "Auto-Detection Engine", reg: "CLIA D5791" },
          { icon: "📝", name: "Intake Form Validation", reg: "ISO 17025 §8.7" },
          { icon: "🕒", name: "Timestamp Logging", reg: "21 CFR §11.10(e)" },
          { icon: "🔌", name: "Instrument Data Pull", reg: "CLSI EP15-A3" },
        ],
      },
      {
        label: "Containment", icon: "🛡️", reg: "ISO 8.7 · 21 CFR 820.100",
        desc: "NEW — expanded CAPA lifecycle. AI drafts immediate containment and correction actions, then scopes the impact assessment: affected records, patients, and lookback window.",
        agent: "Drafting containment plan… lot quarantined, 14-day lookback scoped, 3 departments flagged",
        gate: false, tags: ["ai", "review"],
        section: "CONTAINMENT & IMPACT ASSESSMENT",
        fields: [
          { k: "Containment", v: "LOT-2026-0089 quarantined — analyzer LH-22 offline", c: "hl" },
          { k: "Correction", v: "Affected results flagged in LIMS — hold applied" },
          { k: "Lookback Window", v: "14 days (2026-06-28 → 2026-07-12)", c: "warn" },
          { k: "Records Affected", v: "212 results reviewed — 3 require repeat", c: "warn" },
          { k: "Departments", v: "Hematology · Send-outs · Client Services" },
          { k: "Stage Tracker", v: "containment → correction → impact_assessment ✅", c: "hl" },
        ],
        guardrails: [
          { icon: "🛡️", name: "Containment Scope Validation", reg: "21 CFR 820.100" },
          { icon: "🩺", name: "Patient Lookback Engine", reg: "ISO 15189 §8.7" },
          { icon: "🛑", name: "LIMS Auto-Hold", reg: "CLIA §493.1291" },
          { icon: "🧾", name: "Stage Transition Logging", reg: "21 CFR §11.10(e)" },
        ],
      },
      {
        label: "RCA", icon: "🧠", reg: "ISO 8.7 · CAP GEN.54100",
        desc: "LabComplyx AI agent performs structured 5-Why and Ishikawa analysis using historical deviation data, reagent lot history, and maintenance logs.",
        agent: "Correlating 180-day deviation history… reagent lot correlation: 0.91 confidence",
        gate: false, tags: ["ai", "review"],
        section: "ROOT CAUSE ANALYSIS",
        fields: [
          { k: "RCA Method", v: "5-Why + Ishikawa Fishbone" },
          { k: "Why 1", v: "WBC result exceeded 3σ threshold", c: "warn" },
          { k: "Why 3", v: "Cold-chain exceedance during shipment (48h window)" },
          { k: "Why 5", v: "Incoming QC did not flag cold-chain cert", c: "warn" },
          { k: "Root Cause", v: "VENDOR COLD-CHAIN MONITORING FAILURE", c: "hl" },
          { k: "Lot Correlation Score", v: "0.91 / 1.00", c: "hl" },
          { k: "RCA Status", v: "COMPLETE — Awaiting Human QA Review" },
        ],
        guardrails: [
          { icon: "🧠", name: "5-Why Analysis Engine", reg: "ISO 8.7" },
          { icon: "📊", name: "Historical Correlation", reg: "CAP GEN.54100" },
          { icon: "🔒", name: "Read-Only Audit Access", reg: "21 CFR §11.10(b)" },
          { icon: "📐", name: "RCA Template Enforcement", reg: "ISO 17025 §8.7.2" },
        ],
      },
      {
        label: "CAPA Plan", icon: "📋", reg: "CLIA D5791",
        desc: "AI generates a structured CAPA plan with specific actions, owners, target dates, and effectiveness criteria. All fields mapped to regulation.",
        agent: "Generating CAPA plan… 3 corrective actions, 2 preventive actions drafted",
        gate: false, tags: ["ai", "pending"],
        section: "CAPA PLAN",
        fields: [
          { k: "Plan ID", v: "CAPA-2026-0047-PLAN", c: "hl" },
          { k: "CA-01", v: "Quarantine all units from LOT-2026-0089" },
          { k: "CA-01 Owner", v: "Lab Director — Due: 2026-07-12" },
          { k: "CA-03", v: "Notify ordering physician — critical flag delay", c: "warn" },
          { k: "PA-01", v: "Revise SOP-QC-041: Add incoming cold-chain check" },
          { k: "Effectiveness Criteria", v: "Zero repeat deviation for 90 days", c: "hl" },
        ],
        guardrails: [
          { icon: "🎯", name: "SMART Action Validation", reg: "CAP GEN.54100" },
          { icon: "👤", name: "Owner Assignment Check", reg: "ISO 8.7.3" },
          { icon: "📅", name: "Due Date Enforcement", reg: "CLIA D5791" },
          { icon: "📈", name: "Effectiveness Criteria", reg: "ISO 15189 §8.7" },
        ],
      },
      {
        label: "QA Review", icon: "👤", reg: "FDA CSA 2022 · GAMP 5",
        desc: "MANDATORY human-in-the-loop checkpoint. QA Lead reviews AI-generated RCA and CAPA plan before any action proceeds. AI cannot bypass this gate.",
        agent: "Workflow paused — awaiting QA Lead electronic sign-off (21 CFR Part 11)",
        gate: true, tags: ["pending"],
        section: "HUMAN QA GATE",
        fields: [
          { k: "Gate ID", v: "HUMAN-GATE-QA-001", c: "hl" },
          { k: "Gate Type", v: "🚦 REQUIRED — AI Cannot Bypass", c: "warn" },
          { k: "E-Signature Required", v: "YES — 21 CFR Part 11 compliant", c: "hl" },
          { k: "Status", v: "⏳ AWAITING HUMAN SIGN-OFF", c: "warn" },
        ],
        guardrails: [
          { icon: "🚦", name: "Mandatory Human Gate", reg: "FDA CSA 2022" },
          { icon: "✍️", name: "21 CFR Part 11 E-Sig", reg: "21 CFR §11.50" },
          { icon: "🧾", name: "Immutable Audit Log", reg: "21 CFR §11.10(e)" },
          { icon: "⛔", name: "AI Override Disabled", reg: "GAMP 5 Annex 11" },
        ],
      },
      {
        label: "Effectiveness", icon: "📈", reg: "CLIA D5791 · ISO 8.7",
        desc: "AI monitors effectiveness metrics for 90 days post-implementation. Automated KPI tracking against pre-defined criteria. Alert if deviation recurs.",
        agent: "Monitoring effectiveness… Day 14 of 90 — all KPIs within specification",
        gate: false, tags: ["compliant"],
        section: "EFFECTIVENESS CHECK",
        fields: [
          { k: "Monitoring ID", v: "EFF-2026-0047", c: "hl" },
          { k: "KPI 1", v: "Zero repeat WBC deviation ✅", c: "hl" },
          { k: "KPI 3", v: "SOP-QC-041 Rev 4 training completion: 8/8 ✅", c: "hl" },
          { k: "Trend Status", v: "STABLE — No recurrence signals", c: "hl" },
          { k: "Overall Status", v: "ON TRACK", c: "hl" },
        ],
        guardrails: [
          { icon: "📊", name: "KPI Automated Tracking", reg: "CLIA D5791" },
          { icon: "🚨", name: "Recurrence Alert Engine", reg: "ISO 8.7.4" },
          { icon: "🔒", name: "90-Day Monitoring Lock", reg: "CAP GEN.54100" },
          { icon: "📈", name: "Trend Analysis Log", reg: "ISO 15189 §8.7" },
        ],
      },
      {
        label: "Closure", icon: "✅", reg: "CLIA · CAP · ISO 8.7",
        desc: "CAPA closed through the 8-point closure gate checklist — root cause evidenced, actions verified, effectiveness demonstrated, residual risk accepted. Closure is never automated; an authorized approver signs.",
        agent: "CAPA-2026-0047 closure package compiled — 12 documents, 4 e-signatures recorded",
        gate: false, tags: ["compliant", "signed"],
        section: "FORMAL CLOSURE",
        fields: [
          { k: "Status", v: "✅ FORMALLY CLOSED", c: "hl" },
          { k: "Closure Gate", v: "8/8 checklist items satisfied ✅", c: "hl" },
          { k: "Residual Risk", v: "LOW — accepted with rationale by QA Mgr" },
          { k: "Closed By", v: "Authorized approver — never automated", c: "hl" },
          { k: "Time Saved", v: "4.2 hours AI-assisted documentation", c: "hl" },
          { k: "E-Signatures", v: "4 (QA Lead, Lab Director, Med Director, QA Mgr)", c: "hl" },
          { k: "Audit Trail", v: "47 immutable log entries" },
          { k: "21 CFR Part 11", v: "COMPLIANT ✅", c: "hl" },
          { k: "Retention Tag", v: "6 years — auto-archive 2032-10-26" },
          { k: "CAP Inspection Ready", v: "YES — GEN.54100 package complete", c: "hl" },
          { k: "Final Status", v: "CLOSED — EFFECTIVE", c: "hl" },
        ],
        guardrails: [
          { icon: "📦", name: "Evidence Package Validation", reg: "CAP GEN.54100" },
          { icon: "🧾", name: "Immutable Closure Record", reg: "21 CFR §11.10(e)" },
          { icon: "🗄️", name: "6-Year Retention Tag", reg: "CLIA D5791" },
          { icon: "☑️", name: "Closure Checklist Complete", reg: "ISO 8.7.6" },
        ],
      },
    ],
  },
  {
    id: "sop",
    icon: "📄",
    name: "SOP Management",
    desc: "Full SOP lifecycle — AI drafting to version control",
    regs: ["CAP GEN.20375", "21 CFR §211.68"],
    docId: "SOP-QC-041-REV4",
    timeSaved: "6.8 hrs",
    completionScore: "6.8 hrs saved",
    completionMsg:
      "SOP change ran as a verifiable state machine — AI-drafted, SME-governed, e-signed, training distributed, version locked. Who changed what, and why, is provable at every step. 6.8 hours saved as a byproduct.",
    steps: [
      {
        label: "Trigger", icon: "⚡", reg: "CAP GEN.20375",
        desc: "Revision workflow initiated from a linked CAPA preventive action. Priority and document control requirements auto-assigned.",
        agent: "CAPA PA-01 trigger received… initiating SOP-QC-041 revision workflow",
        gate: false, tags: ["ai", "pending"],
        section: "REVISION TRIGGER",
        fields: [
          { k: "SOP ID", v: "SOP-QC-041-REV4", c: "hl" },
          { k: "Trigger", v: "CAPA-2026-0047 PA-01 (Linked)", c: "hl" },
          { k: "Priority", v: "HIGH — Patient Safety Impact", c: "warn" },
          { k: "CAP Reference", v: "GEN.20375 — Document Control", c: "hl" },
        ],
        guardrails: [
          { icon: "🔗", name: "CAPA Cross-Link Validation", reg: "CAP GEN.54100" },
          { icon: "📂", name: "Document Control Registry", reg: "CAP GEN.20375" },
          { icon: "🕒", name: "Timestamp Logging", reg: "21 CFR §11.10(e)" },
          { icon: "⚖️", name: "Priority Classification", reg: "ISO 15189 §8.3" },
        ],
      },
      {
        label: "AI Draft", icon: "🤖", reg: "GAMP 5 · FDA CSA 2022",
        desc: "LabComplyx Agent drafts the revised SOP section using approved templates and current CLSI guidance. AI drafts · humans validate.",
        agent: "Drafting SOP-QC-041 Rev 4… applying CLSI EP23-A cold-chain guidelines",
        gate: false, tags: ["ai", "review"],
        section: "AI DRAFT",
        fields: [
          { k: "New Section Added", v: "§4.2 — Incoming Cold-Chain Certificate Verification", c: "hl" },
          { k: "New Step 4.2.3", v: "Reject if >2°C exceedance for >2hr; document in LIMS" },
          { k: "Regulatory Gap Addressed", v: "CAPA-2026-0047 Root Cause ✅", c: "hl" },
          { k: "Status", v: "DRAFT — Awaiting SME Review", c: "warn" },
        ],
        guardrails: [
          { icon: "📐", name: "Approved Template Enforcement", reg: "CAP GEN.20375" },
          { icon: "📚", name: "CLSI Guideline Mapping", reg: "CLSI EP23-A" },
          { icon: "🛡️", name: "Draft Watermark Control", reg: "GAMP 5" },
          { icon: "🧾", name: "AI Provenance Logging", reg: "FDA CSA 2022" },
        ],
      },
      {
        label: "SME Review", icon: "🔬", reg: "ISO 17025 §8.3",
        desc: "MANDATORY human-in-the-loop checkpoint. Subject matter experts review the AI draft — human authority is final and AI cannot override SME decisions.",
        agent: "SME review cycle open… awaiting Lab Director and QA Lead review comments",
        gate: true, tags: ["review"],
        section: "SME REVIEW",
        fields: [
          { k: "Gate Type", v: "🚦 HUMAN-IN-LOOP — Required", c: "warn" },
          { k: "Comment 02", v: "QA Mgr: Add step for supervisor notification (OPEN)", c: "warn" },
          { k: "AI Note", v: "Cannot override SME decisions — human authority final" },
          { k: "Status", v: "IN REVIEW — 1 comment pending", c: "warn" },
        ],
        guardrails: [
          { icon: "🚦", name: "Mandatory SME Gate", reg: "ISO 17025 §8.3" },
          { icon: "💬", name: "Comment Resolution Tracking", reg: "CAP GEN.20375" },
          { icon: "⛔", name: "AI Override Disabled", reg: "GAMP 5 Annex 11" },
          { icon: "🧾", name: "Review Cycle Audit Log", reg: "21 CFR §11.10(e)" },
        ],
      },
      {
        label: "E-Sign", icon: "✍️", reg: "21 CFR Part 11",
        desc: "21 CFR Part 11 compliant electronic signatures collected via MFA. Version is hash-sealed and locked on final signature.",
        agent: "Collecting e-signatures… 3 of 3 signatories confirmed via MFA",
        gate: false, tags: ["signed", "compliant"],
        section: "ELECTRONIC SIGNATURES",
        fields: [
          { k: "E-Sig Framework", v: "21 CFR Part 11 — COMPLIANT ✅", c: "hl" },
          { k: "Hash 1", v: "SHA-256: 7f3a9b…e12c4d", c: "hl" },
          { k: "Tamper Seal", v: "APPLIED — SHA-256: cc91…4f2e", c: "hl" },
          { k: "Status", v: "FULLY SIGNED — VERSION LOCKED ✅", c: "hl" },
        ],
        guardrails: [
          { icon: "✍️", name: "MFA E-Signature Capture", reg: "21 CFR §11.50" },
          { icon: "🔏", name: "SHA-256 Tamper Seal", reg: "21 CFR §11.10(c)" },
          { icon: "🔒", name: "Version Lock on Sign", reg: "CAP GEN.20375" },
          { icon: "🧾", name: "Signature Manifest Log", reg: "21 CFR §11.10(e)" },
        ],
      },
      {
        label: "Training", icon: "🎓", reg: "CAP GEN.55500",
        desc: "Training automatically distributed to affected staff with completion tracking and competency documentation.",
        agent: "Distributing training… 8 affected staff notified — deadline 2026-07-26",
        gate: false, tags: ["compliant"],
        section: "TRAINING DISTRIBUTION",
        fields: [
          { k: "Training ID", v: "TRN-SOP-QC-041-REV4", c: "hl" },
          { k: "Completed", v: "8 / 8 ✅", c: "hl" },
          { k: "CAP Reference", v: "GEN.55500 — Personnel Competency", c: "hl" },
        ],
        guardrails: [
          { icon: "🎓", name: "Affected-Staff Detection", reg: "CAP GEN.55500" },
          { icon: "📅", name: "Deadline Enforcement", reg: "CLIA §493.1235" },
          { icon: "☑️", name: "Completion Tracking", reg: "ISO 15189 §6.2" },
          { icon: "🧾", name: "Competency Record Log", reg: "21 CFR §11.10(e)" },
        ],
      },
      {
        label: "Version Ctrl", icon: "🔄", reg: "CAP GEN.20375 · ISO",
        desc: "Rev 4 published as the live controlled document; Rev 3 archived with cross-reference. Closed loop back to the originating CAPA.",
        agent: "SOP-QC-041 Rev 4 published — Rev 3 archived with cross-reference to CAPA-2026-0047",
        gate: false, tags: ["compliant", "signed"],
        section: "VERSION CONTROL",
        fields: [
          { k: "Active Version", v: "SOP-QC-041 Rev 4 — LIVE ✅", c: "hl" },
          { k: "CAPA Cross-Ref", v: "CAPA-2026-0047 PA-01 — CLOSED LOOP ✅", c: "hl" },
          { k: "Time Saved", v: "6.8 hours vs. manual SOP workflow", c: "hl" },
          { k: "Status", v: "VERSION CONTROLLED — AUDIT READY ✅", c: "hl" },
        ],
        guardrails: [
          { icon: "🔄", name: "Single Live Version Rule", reg: "CAP GEN.20375" },
          { icon: "🗄️", name: "Prior Revision Archive", reg: "ISO 15189 §8.3" },
          { icon: "🔗", name: "CAPA Loop Closure Check", reg: "CAP GEN.54100" },
          { icon: "🧾", name: "Immutable Publication Log", reg: "21 CFR §11.10(e)" },
        ],
      },
    ],
  },
  {
    id: "deviation",
    icon: "⚠️",
    name: "Deviation Documentation",
    desc: "Structured intake through QA approval and archive",
    regs: ["CLIA D5803", "ISO 17025 §8.7"],
    docId: "DEV-2026-0091",
    timeSaved: "3.1 hrs",
    completionScore: "3.1 hrs saved",
    completionMsg:
      "The deviation investigated itself — RCA, patient impact, and remediation arrived pre-drafted at the QA approval gate, self-documented and archived in 1 day. 3.1 hours saved as a byproduct.",
    steps: [
      {
        label: "Intake", icon: "📥", reg: "CLIA D5803",
        desc: "Structured deviation intake with AI severity classification and automatic routing to QA.",
        agent: "Deviation DEV-2026-0091 received… AI classifying severity: MAJOR (equipment failure)",
        gate: false, tags: ["ai", "pending"],
        section: "DEVIATION INTAKE",
        fields: [
          { k: "Deviation ID", v: "DEV-2026-0091", c: "hl" },
          { k: "AI Severity", v: "MAJOR — Equipment Failure / Patient Impact Risk", c: "warn" },
          { k: "Equipment", v: "Centrifuge C-07" },
          { k: "Status", v: "INTAKE COMPLETE — Routing to QA", c: "hl" },
        ],
        guardrails: [
          { icon: "📥", name: "Structured Intake Form", reg: "CLIA D5803" },
          { icon: "⚖️", name: "AI Severity Classifier", reg: "ISO 17025 §8.7" },
          { icon: "🕒", name: "Timestamp Logging", reg: "21 CFR §11.10(e)" },
          { icon: "📮", name: "QA Routing Rules", reg: "ISO 15189 §8.7" },
        ],
      },
      {
        label: "Impact", icon: "🩺", reg: "ISO 15189 §8.7",
        desc: "AI assesses patient impact across affected samples and applies automatic result holds in LIMS until resolution.",
        agent: "Assessing patient impact… 3 STAT samples: 2 retestable, 1 requires recollect",
        gate: false, tags: ["ai", "review"],
        section: "PATIENT IMPACT ASSESSMENT",
        fields: [
          { k: "Samples Affected", v: "3 STAT samples" },
          { k: "Sample S22-0093", v: "STAT Troponin — RECOLLECT REQUIRED", c: "warn" },
          { k: "Result Reportability", v: "HOLD — do not report until resolved", c: "hl" },
          { k: "LIMS Flag", v: "Auto-hold applied", c: "hl" },
        ],
        guardrails: [
          { icon: "🩺", name: "Patient Impact Matrix", reg: "ISO 15189 §8.7" },
          { icon: "🛑", name: "LIMS Auto-Hold", reg: "CLIA §493.1291" },
          { icon: "📋", name: "Sample Traceability", reg: "ISO 17025 §7.5" },
          { icon: "🧾", name: "Impact Decision Log", reg: "21 CFR §11.10(e)" },
        ],
      },
      {
        label: "RCA", icon: "🧠", reg: "ISO 17025 §8.7",
        desc: "AI reviews equipment maintenance history and correlates deviation cause. A linked CAPA is auto-initiated when a systemic gap is found.",
        agent: "Reviewing centrifuge C-07 maintenance history… last PM: 6 months ago (overdue)",
        gate: false, tags: ["ai", "review"],
        section: "ROOT CAUSE ANALYSIS",
        fields: [
          { k: "Equipment History", v: "C-07 — last PM 6 months ago (overdue)", c: "warn" },
          { k: "Root Cause", v: "PREVENTIVE MAINTENANCE SCHEDULE GAP", c: "warn" },
          { k: "CAPA Initiated", v: "CAPA-2026-0052 (auto-linked)", c: "hl" },
        ],
        guardrails: [
          { icon: "🧠", name: "Maintenance History Scan", reg: "CAP COM.30675" },
          { icon: "🔗", name: "Auto CAPA Linkage", reg: "CAP GEN.54100" },
          { icon: "📐", name: "RCA Template Enforcement", reg: "ISO 17025 §8.7.2" },
          { icon: "🧾", name: "Read-Only Audit Access", reg: "21 CFR §11.10(b)" },
        ],
      },
      {
        label: "Remediation", icon: "🔧", reg: "CLIA D5803",
        desc: "Remediation tracked to closure: equipment service, retests, recollections, and physician notification with timestamps.",
        agent: "Remediation tracking: C-07 serviced ✅ · S22-0091/92 retested ✅ · S22-0093 recollect initiated",
        gate: false, tags: ["review", "pending"],
        section: "REMEDIATION TRACKING",
        fields: [
          { k: "Equipment", v: "C-07 serviced and requalified ✅" },
          { k: "Physician Notified", v: "Dr. A. Patel — 12:47 UTC ✅", c: "hl" },
          { k: "LIMS Result Hold", v: "Released for S22-0091, S22-0092 ✅" },
          { k: "S22-0093", v: "Recollection initiated", c: "warn" },
        ],
        guardrails: [
          { icon: "🔧", name: "Remediation Task Tracking", reg: "CLIA D5803" },
          { icon: "📞", name: "Physician Notification Log", reg: "CLIA §493.1291(g)" },
          { icon: "🛑", name: "Hold-Release Control", reg: "ISO 15189 §7.4" },
          { icon: "🕒", name: "Timestamp Logging", reg: "21 CFR §11.10(e)" },
        ],
      },
      {
        label: "QA Approval", icon: "👤", reg: "ISO 15189 §8.7",
        desc: "MANDATORY human-in-the-loop checkpoint. QA package assembled for Lab Director e-signature — AI cannot close the deviation.",
        agent: "QA approval package assembled — awaiting Lab Director e-signature",
        gate: true, tags: ["compliant", "signed"],
        section: "QA APPROVAL",
        fields: [
          { k: "Patient Outcome", v: "No adverse outcome ✅", c: "hl" },
          { k: "Reportable Event?", v: "NO — contained", c: "hl" },
          { k: "E-Signature", v: "SHA-256: f9a2…7d34 — QA Lead", c: "hl" },
        ],
        guardrails: [
          { icon: "🚦", name: "Mandatory QA Gate", reg: "ISO 15189 §8.7" },
          { icon: "✍️", name: "21 CFR Part 11 E-Sig", reg: "21 CFR §11.50" },
          { icon: "📦", name: "Approval Package Check", reg: "CLIA D5803" },
          { icon: "⛔", name: "AI Closure Disabled", reg: "FDA CSA 2022" },
        ],
      },
      {
        label: "Archive", icon: "🗄️", reg: "CLIA D5803",
        desc: "Deviation archived with linked CAPA and 6-year retention. Immutable audit trail preserved for inspection.",
        agent: "DEV-2026-0091 archived — linked to CAPA-2026-0052 — 6-year retention applied",
        gate: false, tags: ["compliant", "signed"],
        section: "ARCHIVE",
        fields: [
          { k: "Total Duration", v: "1 day (vs avg 4.2 days manual)", c: "hl" },
          { k: "Time Saved", v: "3.1 hours AI-assisted", c: "hl" },
          { k: "Linked CAPA", v: "CAPA-2026-0052", c: "hl" },
          { k: "Retention", v: "6 years — auto-archive applied" },
        ],
        guardrails: [
          { icon: "🗄️", name: "6-Year Retention Tag", reg: "CLIA D5803" },
          { icon: "🔗", name: "CAPA Cross-Reference", reg: "CAP GEN.54100" },
          { icon: "🧾", name: "Immutable Archive Record", reg: "21 CFR §11.10(e)" },
          { icon: "☑️", name: "Closure Checklist Complete", reg: "ISO 17025 §8.7" },
        ],
      },
    ],
  },
  {
    id: "inspection",
    icon: "🛡️",
    name: "Inspection Readiness",
    desc: "CAP/CLIA preparation — gap scan to inspector-ready",
    regs: ["CAP GEN.20000", "ISO 8.9.1"],
    docId: "INSP-2026-Q3",
    timeSaved: "12.4 hrs",
    extraStat: "Score: 97.8%",
    completionScore: "97.8%",
    completionMsg:
      "Inspectors get a verifiable evidence system, not a folder of documents — readiness was built as a byproduct of the work. All guardrails satisfied, human gates cleared, Part 11 e-signatures recorded. 12.4 hours saved.",
    steps: [
      {
        label: "Gap Scan", icon: "🔍", reg: "CAP GEN.20000",
        desc: "AI scans the full document inventory against every applicable regulatory requirement to surface gaps by severity.",
        agent: "Scanning 847 documents against 2,341 regulatory requirements… 23 gaps identified",
        gate: false, tags: ["ai", "pending"],
        section: "GAP SCAN",
        fields: [
          { k: "Documents Scanned", v: "847", c: "hl" },
          { k: "Requirements Checked", v: "2,341" },
          { k: "Total Gaps", v: "23", c: "warn" },
          { k: "Critical Gaps", v: "3 (immediate action required)", c: "warn" },
        ],
        guardrails: [
          { icon: "🔍", name: "Full-Inventory Scan", reg: "CAP GEN.20000" },
          { icon: "📚", name: "Requirement Mapping Engine", reg: "CLIA §493" },
          { icon: "⚖️", name: "Gap Severity Grading", reg: "ISO 8.9.1" },
          { icon: "🧾", name: "Scan Result Logging", reg: "21 CFR §11.10(e)" },
        ],
      },
      {
        label: "Remediation", icon: "🔧", reg: "ISO 8.9.1",
        desc: "Remediation tasks auto-assigned by gap severity with owner tracking and progress rollup.",
        agent: "Remediation tasks assigned… 3 critical gaps: 2 resolved, 1 in progress",
        gate: false, tags: ["review", "pending"],
        section: "GAP REMEDIATION",
        fields: [
          { k: "P1 Status", v: "COMPLETE — All 5 signed ✅", c: "hl" },
          { k: "Critical Gaps", v: "2 resolved, 1 in progress", c: "warn" },
          { k: "Overall Remediation", v: "91% complete", c: "hl" },
        ],
        guardrails: [
          { icon: "🎯", name: "Severity-Based Assignment", reg: "ISO 8.9.1" },
          { icon: "👤", name: "Owner Accountability Check", reg: "CAP GEN.20000" },
          { icon: "📅", name: "Due Date Enforcement", reg: "CLIA §493.1239" },
          { icon: "🧾", name: "Remediation Audit Log", reg: "21 CFR §11.10(e)" },
        ],
      },
      {
        label: "Doc Package", icon: "📦", reg: "CAP GEN.20000",
        desc: "AI compiles the full inspection package organized by CAP checklist section with completeness scoring.",
        agent: "Compiling inspection package… 847 documents organized into 12 CAP checklist sections",
        gate: false, tags: ["ai", "compliant"],
        section: "DOCUMENT PACKAGE",
        fields: [
          { k: "Total Documents", v: "847 documents compiled", c: "hl" },
          { k: "Checklist Sections", v: "12 CAP sections organized" },
          { k: "Completeness", v: "98.7%", c: "hl" },
        ],
        guardrails: [
          { icon: "📦", name: "Checklist-Section Mapping", reg: "CAP GEN.20000" },
          { icon: "☑️", name: "Completeness Scoring", reg: "ISO 8.9.1" },
          { icon: "🔏", name: "Package Integrity Seal", reg: "21 CFR §11.10(c)" },
          { icon: "🧾", name: "Compilation Audit Log", reg: "21 CFR §11.10(e)" },
        ],
      },
      {
        label: "Mock Inspection", icon: "🎤", reg: "CAP GEN.20000",
        desc: "MANDATORY human-in-the-loop checkpoint. AI simulates a CAP inspector interview — the QA Lead must clear all flagged items before proceeding.",
        agent: "Simulating CAP inspector… 42 questions asked — 40 answered correctly, 2 flagged",
        gate: true, tags: ["review", "pending"],
        section: "MOCK INSPECTION",
        fields: [
          { k: "Questions Asked", v: "42" },
          { k: "Correct Answers", v: "40 / 42", c: "hl" },
          { k: "Predicted Score", v: "97.8% readiness", c: "hl" },
          { k: "Human Review Gate", v: "🚦 QA Lead must clear 2 flagged items", c: "warn" },
        ],
        guardrails: [
          { icon: "🚦", name: "Flagged-Item Human Gate", reg: "FDA CSA 2022" },
          { icon: "🎤", name: "Inspector Simulation Engine", reg: "CAP GEN.20000" },
          { icon: "📊", name: "Readiness Scoring Model", reg: "ISO 8.9.1" },
          { icon: "🧾", name: "Interview Transcript Log", reg: "21 CFR §11.10(e)" },
        ],
      },
      {
        label: "Reg Map", icon: "🗺️", reg: "CLIA · CAP · ISO",
        desc: "Full regulatory cross-reference map generated — every document mapped to every applicable requirement with zero unmapped items.",
        agent: "Generating regulatory map… 847 documents cross-referenced to 2,341 requirements",
        gate: false, tags: ["compliant"],
        section: "REGULATORY MAP",
        fields: [
          { k: "CAP Checklist", v: "100% items mapped ✅", c: "hl" },
          { k: "CLIA Standards", v: "100% items mapped ✅", c: "hl" },
          { k: "Unmapped Items", v: "0 — complete coverage", c: "hl" },
        ],
        guardrails: [
          { icon: "🗺️", name: "Cross-Reference Engine", reg: "CAP GEN.20000" },
          { icon: "☑️", name: "Coverage Completeness Check", reg: "CLIA §493" },
          { icon: "📚", name: "Requirement Version Control", reg: "ISO 8.9.1" },
          { icon: "🧾", name: "Map Generation Log", reg: "21 CFR §11.10(e)" },
        ],
      },
      {
        label: "Archive", icon: "🗄️", reg: "CAP GEN.20000",
        desc: "Inspection package finalized and archived — inspector-ready with immutable audit trail.",
        agent: "Inspection package finalized — 97.8% readiness score — 12.4 hours saved",
        gate: false, tags: ["compliant", "signed"],
        section: "FINAL PACKAGE",
        fields: [
          { k: "Readiness Score", v: "97.8% ✅", c: "hl" },
          { k: "Documents Ready", v: "847 / 847 (100%)", c: "hl" },
          { k: "Time Saved", v: "12.4 hours vs. manual preparation", c: "hl" },
        ],
        guardrails: [
          { icon: "📦", name: "Final Package Validation", reg: "CAP GEN.20000" },
          { icon: "🔏", name: "Package Tamper Seal", reg: "21 CFR §11.10(c)" },
          { icon: "🗄️", name: "Retention Tagging", reg: "CLIA §493.1105" },
          { icon: "🧾", name: "Immutable Archive Record", reg: "21 CFR §11.10(e)" },
        ],
      },
    ],
  },
  {
    id: "validation",
    icon: "✅",
    name: "Validation Support",
    desc: "IQ/OQ/PQ lifecycle with GAMP 5 and 21 CFR Part 11",
    regs: ["GAMP 5 Cat 5", "21 CFR §211.68"],
    docId: "VAL-2026-LIMS-01",
    timeSaved: "18.2 hrs",
    completionScore: "18.2 hrs saved",
    completionMsg:
      "Validation ran as an automated artifact pipeline — IQ/OQ/PQ records generated, linked to risk-based acceptance criteria, and verified at human checkpoints. GAMP 5 Cat 5 validated, Part 11 compliant, production approved. 18.2 hours saved as a byproduct.",
    steps: [
      {
        label: "Risk Assess", icon: "⚖️", reg: "GAMP 5 Cat 5",
        desc: "System classified per GAMP 5 with URS and risk matrix generated. Risk level drives the validation rigor applied downstream.",
        agent: "Classifying LIMS as GAMP 5 Category 5… generating URS and risk matrix",
        gate: false, tags: ["ai", "pending"],
        section: "RISK ASSESSMENT",
        fields: [
          { k: "System", v: "LIMS — Laboratory Information Management System" },
          { k: "GAMP 5 Category", v: "Category 5", c: "hl" },
          { k: "Risk Level", v: "HIGH — Direct patient impact", c: "warn" },
          { k: "URS Status", v: "Generated — 112 requirements" },
        ],
        guardrails: [
          { icon: "⚖️", name: "GAMP 5 Classification", reg: "GAMP 5" },
          { icon: "📋", name: "URS Template Enforcement", reg: "GAMP 5 Cat 5" },
          { icon: "📊", name: "Risk Matrix Validation", reg: "ISO 14971" },
          { icon: "🧾", name: "Assessment Audit Log", reg: "21 CFR §11.10(e)" },
        ],
      },
      {
        label: "IQ", icon: "🔩", reg: "GAMP 5 · 21 CFR §211.68",
        desc: "Installation Qualification protocol executed with every check verified against specification.",
        agent: "IQ protocol executing… 34/34 checks passed — 0 deviations",
        gate: false, tags: ["ai", "compliant"],
        section: "INSTALLATION QUALIFICATION",
        fields: [
          { k: "IQ Checks Passed", v: "34 / 34 ✅", c: "hl" },
          { k: "IQ Deviations", v: "0 — Clean installation", c: "hl" },
          { k: "Protocol", v: "VAL-2026-LIMS-01-IQ" },
        ],
        guardrails: [
          { icon: "🔩", name: "IQ Protocol Enforcement", reg: "GAMP 5" },
          { icon: "☑️", name: "Check-by-Check Verification", reg: "21 CFR §211.68" },
          { icon: "📸", name: "Evidence Capture", reg: "GAMP 5 Cat 5" },
          { icon: "🧾", name: "Execution Audit Log", reg: "21 CFR §11.10(e)" },
        ],
      },
      {
        label: "OQ", icon: "🧪", reg: "GAMP 5 · 21 CFR Part 11",
        desc: "Operational Qualification test cases executed including full verification of e-signature functionality.",
        agent: "OQ testing: 92/94 test cases passed — 2 minor deviations logged and resolved",
        gate: false, tags: ["ai", "review"],
        section: "OPERATIONAL QUALIFICATION",
        fields: [
          { k: "Test Cases", v: "92 / 94 passed — 2 minor deviations resolved" },
          { k: "21 CFR Part 11", v: "E-signature functionality fully verified ✅", c: "hl" },
          { k: "OQ Summary", v: "PASS — 97.9% test case success", c: "hl" },
        ],
        guardrails: [
          { icon: "🧪", name: "OQ Test Case Traceability", reg: "GAMP 5" },
          { icon: "✍️", name: "E-Sig Function Verification", reg: "21 CFR Part 11" },
          { icon: "⚠️", name: "Deviation Logging", reg: "ISO 17025 §8.7" },
          { icon: "🧾", name: "Test Evidence Log", reg: "21 CFR §11.10(e)" },
        ],
      },
      {
        label: "PQ", icon: "🏭", reg: "GAMP 5",
        desc: "Performance Qualification: parallel production operation with accuracy and availability monitored against spec.",
        agent: "PQ running: 5-day parallel operation complete — all production metrics within spec",
        gate: false, tags: ["ai", "compliant"],
        section: "PERFORMANCE QUALIFICATION",
        fields: [
          { k: "Parallel Operation", v: "5 days complete" },
          { k: "Result Accuracy", v: "100%", c: "hl" },
          { k: "System Availability", v: "99.97% uptime", c: "hl" },
          { k: "PQ Deviations", v: "0 ✅", c: "hl" },
        ],
        guardrails: [
          { icon: "🏭", name: "Parallel Run Monitoring", reg: "GAMP 5" },
          { icon: "🎯", name: "Accuracy Threshold Check", reg: "CLIA §493.1253" },
          { icon: "⏱️", name: "Availability SLA Tracking", reg: "GAMP 5 Cat 5" },
          { icon: "🧾", name: "Metric Capture Log", reg: "21 CFR §11.10(e)" },
        ],
      },
      {
        label: "21 CFR §11", icon: "📜", reg: "21 CFR Part 11",
        desc: "MANDATORY human-in-the-loop checkpoint. Full 21 CFR Part 11 assessment complete — awaiting QA sign-off before production approval.",
        agent: "21 CFR Part 11 assessment: 67/67 requirements satisfied — full compliance confirmed",
        gate: true, tags: ["compliant", "pending"],
        section: "PART 11 ASSESSMENT",
        fields: [
          { k: "Requirements Met", v: "67 / 67 — FULL COMPLIANCE ✅", c: "hl" },
          { k: "Status", v: "Awaiting QA Sign-Off", c: "warn" },
        ],
        guardrails: [
          { icon: "🚦", name: "Mandatory QA Gate", reg: "FDA CSA 2022" },
          { icon: "📜", name: "Part 11 Requirement Matrix", reg: "21 CFR Part 11" },
          { icon: "✍️", name: "E-Signature Readiness", reg: "21 CFR §11.50" },
          { icon: "🧾", name: "Assessment Audit Log", reg: "21 CFR §11.10(e)" },
        ],
      },
      {
        label: "Archive", icon: "🗄️", reg: "GAMP 5 · 21 CFR Part 11",
        desc: "Validation package finalized, sealed, and archived. System formally approved for production use.",
        agent: "Validation package finalized — 18.2 hours saved — system approved for production",
        gate: false, tags: ["compliant", "signed"],
        section: "VALIDATION PACKAGE",
        fields: [
          { k: "Validation Status", v: "✅ VALIDATED — Approved for Production", c: "hl" },
          { k: "Time Saved", v: "18.2 hours AI-assisted validation", c: "hl" },
          { k: "Package", v: "IQ + OQ + PQ + Part 11 assessment sealed" },
        ],
        guardrails: [
          { icon: "📦", name: "Package Completeness Check", reg: "GAMP 5" },
          { icon: "🔏", name: "Tamper Seal Applied", reg: "21 CFR §11.10(c)" },
          { icon: "🗄️", name: "Retention Tagging", reg: "21 CFR §211.180" },
          { icon: "🧾", name: "Immutable Archive Record", reg: "21 CFR §11.10(e)" },
        ],
      },
    ],
  },
  {
    id: "aigov",
    icon: "🤖",
    name: "AI Governance",
    desc: "AI model lifecycle — registration to certification",
    regs: ["GAMP 5 Annex 11", "FDA CSA 2022"],
    docId: "AIGOV-2026-0012",
    timeSaved: "9.6 hrs",
    completionScore: "9.6 hrs saved",
    completionMsg:
      "The AI itself is inspection-ready — bias validated, 3-party human approval gate cleared, every decision immutably logged, transparency certified. Governance the AI cannot bypass. 9.6 hours saved as a byproduct.",
    steps: [
      {
        label: "Registration", icon: "📇", reg: "FDA CSA 2022",
        desc: "AI model registered with documented intended use, training provenance, performance baseline, and risk classification.",
        agent: "Registering AI Model: RCA-Agent v2.1 — documenting intended use and training provenance",
        gate: false, tags: ["ai", "pending"],
        section: "MODEL REGISTRATION",
        fields: [
          { k: "Registry ID", v: "AIGOV-2026-0012", c: "hl" },
          { k: "Model", v: "RCA-Agent v2.1" },
          { k: "Performance Baseline", v: "Precision 91.4% · F1 90.0%", c: "hl" },
          { k: "Risk Classification", v: "HIGH — GxP clinical context", c: "warn" },
        ],
        guardrails: [
          { icon: "📇", name: "Model Registry Enforcement", reg: "FDA CSA 2022" },
          { icon: "📚", name: "Training Provenance Record", reg: "GAMP 5 Annex 11" },
          { icon: "🎯", name: "Baseline Metric Capture", reg: "FDA AI/ML 2021" },
          { icon: "🧾", name: "Registration Audit Log", reg: "21 CFR §11.10(e)" },
        ],
      },
      {
        label: "Bias Check", icon: "⚖️", reg: "FDA AI/ML 2021",
        desc: "Bias analysis across demographic and analytical variables with confidence calibration scoring.",
        agent: "Bias analysis running… testing across 12 demographic and analytical variables",
        gate: false, tags: ["ai", "review"],
        section: "BIAS VALIDATION",
        fields: [
          { k: "Variables Tested", v: "12 demographic and analytical variables" },
          { k: "Bias Validation", v: "PASSED ✅", c: "hl" },
          { k: "Confidence Calibration", v: "Brier score: 0.083", c: "hl" },
        ],
        guardrails: [
          { icon: "⚖️", name: "Multi-Variable Bias Testing", reg: "FDA AI/ML 2021" },
          { icon: "📊", name: "Calibration Scoring", reg: "GAMP 5 Annex 11" },
          { icon: "🔬", name: "Test Dataset Controls", reg: "FDA CSA 2022" },
          { icon: "🧾", name: "Bias Result Logging", reg: "21 CFR §11.10(e)" },
        ],
      },
      {
        label: "Human Gate", icon: "🚦", reg: "FDA CSA 2022",
        desc: "MANDATORY human-in-the-loop checkpoint. Three required approvers must sign before clinical deployment — AI cannot self-authorize deployment.",
        agent: "Governance gate active — awaiting 3 required approvals before clinical deployment",
        gate: true, tags: ["pending"],
        section: "GOVERNANCE GATE",
        fields: [
          { k: "Gate Principle", v: "AI CANNOT SELF-AUTHORIZE DEPLOYMENT", c: "warn" },
          { k: "Approvers Required", v: "QA Leader · Lab Director · Compliance Officer" },
          { k: "Deployment Hold", v: "Active — system locked", c: "warn" },
        ],
        guardrails: [
          { icon: "🚦", name: "3-Party Approval Gate", reg: "FDA CSA 2022" },
          { icon: "🔒", name: "Deployment Lock", reg: "GAMP 5 Annex 11" },
          { icon: "✍️", name: "21 CFR Part 11 E-Sig", reg: "21 CFR §11.50" },
          { icon: "⛔", name: "AI Self-Authorization Disabled", reg: "FDA CSA 2022" },
        ],
      },
      {
        label: "Audit Log", icon: "🧾", reg: "21 CFR §11.10(e)",
        desc: "Immutable append-only audit logging of every AI decision, with all human override events recorded with rationale.",
        agent: "Audit logging active… RCA-Agent: 847 decisions logged, avg confidence 91.4%",
        gate: false, tags: ["compliant"],
        section: "IMMUTABLE AUDIT TRAIL",
        fields: [
          { k: "Log System", v: "Immutable append-only — AWS QLDB", c: "hl" },
          { k: "Log Coverage", v: "100% of AI actions — no gaps ✅", c: "hl" },
          { k: "Human Override Events", v: "7 (all logged with rationale)", c: "hl" },
        ],
        guardrails: [
          { icon: "🧾", name: "Append-Only Log Engine", reg: "21 CFR §11.10(e)" },
          { icon: "☑️", name: "100% Coverage Check", reg: "FDA CSA 2022" },
          { icon: "👤", name: "Override Rationale Capture", reg: "GAMP 5 Annex 11" },
          { icon: "🔏", name: "Log Integrity Hashing", reg: "21 CFR §11.10(c)" },
        ],
      },
      {
        label: "Transparency", icon: "🔎", reg: "FDA CSA 2022",
        desc: "30-day transparency report generated: real-world accuracy versus human QA review, drift monitoring, and documented limitations.",
        agent: "Generating transparency report… RCA-Agent v2.1: 30-day performance review complete",
        gate: false, tags: ["ai", "review"],
        section: "TRANSPARENCY REPORT",
        fields: [
          { k: "Accuracy vs. QA Review", v: "94.7%", c: "hl" },
          { k: "Performance Drift", v: "None detected — stable ✅", c: "hl" },
          { k: "Limitations", v: "Novel reagent lot types: lower confidence", c: "warn" },
        ],
        guardrails: [
          { icon: "🔎", name: "30-Day Review Cycle", reg: "FDA CSA 2022" },
          { icon: "📉", name: "Drift Detection Engine", reg: "FDA AI/ML 2021" },
          { icon: "📋", name: "Limitation Disclosure", reg: "GAMP 5 Annex 11" },
          { icon: "🧾", name: "Report Audit Log", reg: "21 CFR §11.10(e)" },
        ],
      },
      {
        label: "Cert", icon: "🏅", reg: "GAMP 5 · FDA CSA 2022",
        desc: "Model formally certified for production with full governance evidence package and immutable certification record.",
        agent: "AIGOV-2026-0012 certification complete — 9.6 hours saved — certified for production",
        gate: false, tags: ["compliant", "signed"],
        section: "CERTIFICATION",
        fields: [
          { k: "Cert ID", v: "AIGOV-2026-0012-CERT", c: "hl" },
          { k: "Model", v: "RCA-Agent v2.1", c: "hl" },
          { k: "Status", v: "✅ CERTIFIED — Production Approved", c: "hl" },
          { k: "Time Saved", v: "9.6 hours AI-assisted governance", c: "hl" },
        ],
        guardrails: [
          { icon: "🏅", name: "Certification Evidence Check", reg: "GAMP 5" },
          { icon: "🔏", name: "Immutable Cert Record", reg: "21 CFR §11.10(e)" },
          { icon: "📅", name: "Recertification Scheduling", reg: "FDA CSA 2022" },
          { icon: "☑️", name: "Governance Checklist Complete", reg: "GAMP 5 Annex 11" },
        ],
      },
    ],
  },
  {
    id: "spine",
    icon: "🧬",
    name: "Traceability Spine",
    desc: "NEW — Gap → Evidence → Risk → CAPA, one connected chain",
    regs: ["CLIA §493", "CAP GEN.20000", "ISO 15189"],
    docId: "GAP-2026-0134",
    timeSaved: "7.5 hrs",
    completionScore: "7.5 hrs saved",
    completionMsg:
      "One gap, one chain — requirement, process, control, evidence, risk, and CAPA all linked with zero duplicate data entry. An inspector can walk the full chain in either direction. 7.5 hours saved as a byproduct.",
    steps: [
      {
        label: "Lab Profile", icon: "🏥", reg: "CLIA §493 · CAP",
        desc: "Laboratory profile drives applicability: AI proposes which frameworks and requirements apply, but a human reviewer must confirm every decision — never auto-finalized.",
        agent: "Analyzing lab profile… CLIA + CAP + ISO 15189 proposed applicable — 3 decisions awaiting reviewer confirmation",
        gate: false, tags: ["ai", "pending"],
        section: "APPLICABILITY SCOPING",
        fields: [
          { k: "Laboratory", v: "Meridian Clinical Diagnostics — high complexity", c: "hl" },
          { k: "Frameworks Proposed", v: "CLIA · CAP · ISO 15189" },
          { k: "Decision Status", v: "needs_confirmation — human review required", c: "warn" },
          { k: "Rationale Captured", v: "Per-framework rationale documented ✅", c: "hl" },
        ],
        guardrails: [
          { icon: "🏥", name: "Profile-Driven Scoping", reg: "CLIA §493" },
          { icon: "👤", name: "Reviewer Confirmation Required", reg: "CAP GEN.20000" },
          { icon: "📋", name: "Rationale Enforcement", reg: "ISO 15189 §4.1" },
          { icon: "🧾", name: "Decision Audit Log", reg: "21 CFR §11.10(e)" },
        ],
      },
      {
        label: "Gap Found", icon: "🔍", reg: "ISO 15189 §8.7",
        desc: "A gap is recorded as expected vs. actual practice, classified with reasoning and a confidence level — never a score alone. Linked to its source requirement and process.",
        agent: "Gap GAP-2026-0134 recorded… expected vs. actual compared — classification: MAJOR, confidence: HIGH",
        gate: false, tags: ["ai", "review"],
        section: "GAP RECORD",
        fields: [
          { k: "Gap ID", v: "GAP-2026-0134", c: "hl" },
          { k: "Expected", v: "Monthly internal audit per QMS schedule" },
          { k: "Actual", v: "No internal audit performed in 5 months", c: "warn" },
          { k: "Classification", v: "MAJOR — with written rationale", c: "warn" },
          { k: "Confidence", v: "HIGH — verified_fact basis", c: "hl" },
          { k: "Linked To", v: "Requirement CAP GEN.13806 · Process #9 Internal Audit", c: "hl" },
        ],
        guardrails: [
          { icon: "⚖️", name: "Rationale-Required Classification", reg: "ISO 15189 §8.7" },
          { icon: "🔗", name: "Requirement Linkage Check", reg: "CAP GEN.20000" },
          { icon: "📊", name: "Confidence-Level Scoring", reg: "FDA CSA 2022" },
          { icon: "🧾", name: "Gap Record Logging", reg: "21 CFR §11.10(e)" },
        ],
      },
      {
        label: "Evidence", icon: "📎", reg: "ISO 15189 · CAP",
        desc: "Evidence collected into a central repository and strength-scored across 9 dimensions — directness, recency, authenticity, traceability, and more.",
        agent: "Scoring evidence… 4 artifacts attached — average strength 4.2/5 across 9 dimensions",
        gate: false, tags: ["ai", "review"],
        section: "EVIDENCE REPOSITORY",
        fields: [
          { k: "Artifacts", v: "Audit schedule · QMS SOP · training log · interview note" },
          { k: "Strength Score", v: "4.2 / 5.0", c: "hl" },
          { k: "Weakest Factor", v: "Recency — schedule last updated 2025-11", c: "warn" },
          { k: "Retention", v: "Classification + retention period tagged ✅", c: "hl" },
        ],
        guardrails: [
          { icon: "📎", name: "9-Factor Strength Scoring", reg: "ISO 15189 §8.4" },
          { icon: "🔒", name: "Confidentiality Classification", reg: "HIPAA / PHI" },
          { icon: "🗄️", name: "Retention Period Tagging", reg: "CLIA §493.1105" },
          { icon: "🧾", name: "Evidence Chain Logging", reg: "21 CFR §11.10(e)" },
        ],
      },
      {
        label: "Risk", icon: "⚠️", reg: "ISO 14971 · ISO 15189",
        desc: "Risk assessed with severity × probability plus a written rationale — never a score alone. Linked to the failed control and the affected quality process.",
        agent: "Assessing risk… severity HIGH × probability PROBABLE — narrative rationale drafted for reviewer",
        gate: false, tags: ["ai", "review"],
        section: "RISK ASSESSMENT",
        fields: [
          { k: "Severity", v: "HIGH — undetected systemic nonconformity", c: "warn" },
          { k: "Probability", v: "PROBABLE — no compensating control active", c: "warn" },
          { k: "Rationale", v: "Written narrative required — score alone rejected", c: "hl" },
          { k: "Failed Control", v: "Internal audit scheduling control — bypassed", c: "warn" },
        ],
        guardrails: [
          { icon: "📐", name: "Rationale-Required Assessment", reg: "ISO 14971" },
          { icon: "🔗", name: "Control Linkage Check", reg: "ISO 15189 §8.5" },
          { icon: "⚖️", name: "Severity Matrix Validation", reg: "CAP GEN.20000" },
          { icon: "🧾", name: "Risk Decision Log", reg: "21 CFR §11.10(e)" },
        ],
      },
      {
        label: "CAPA Decision", icon: "👤", reg: "FDA CSA 2022 · ISO 8.7",
        desc: "MANDATORY human-in-the-loop checkpoint. A human decides: CAPA required, correction only, accepted risk, or no action. AI recommends — it never decides.",
        agent: "Decision package assembled — awaiting QA Lead determination: CAPA required vs. correction only",
        gate: true, tags: ["pending"],
        section: "CAPA DECISION GATE",
        fields: [
          { k: "Gate Type", v: "🚦 HUMAN DECISION — AI recommends only", c: "warn" },
          { k: "AI Recommendation", v: "capa_required — systemic scope" },
          { k: "Options", v: "CAPA · correction only · accepted risk · no action" },
          { k: "Status", v: "⏳ AWAITING HUMAN DETERMINATION", c: "warn" },
        ],
        guardrails: [
          { icon: "🚦", name: "Mandatory Decision Gate", reg: "FDA CSA 2022" },
          { icon: "✍️", name: "21 CFR Part 11 E-Sig", reg: "21 CFR §11.50" },
          { icon: "⛔", name: "AI Decision Disabled", reg: "GAMP 5 Annex 11" },
          { icon: "🧾", name: "Decision Rationale Capture", reg: "21 CFR §11.10(e)" },
        ],
      },
      {
        label: "Process Health", icon: "📈", reg: "ISO 15189 · CAP",
        desc: "CAPA spawned and linked back through the full chain. Process health rollup updates automatically — the spine is walkable end-to-end for any inspector.",
        agent: "CAPA-2026-0061 created and linked — process #9 health rollup updated — full chain verified",
        gate: false, tags: ["compliant", "signed"],
        section: "SPINE CLOSURE",
        fields: [
          { k: "Chain", v: "Requirement → Process → Control → Gap → Evidence → Risk → CAPA ✅", c: "hl" },
          { k: "Linked CAPA", v: "CAPA-2026-0061 (auto-linked, zero re-entry)", c: "hl" },
          { k: "Process Health", v: "Internal Audit process — rollup updated", c: "hl" },
          { k: "Inspector View", v: "Chain walkable in both directions ✅", c: "hl" },
          { k: "Time Saved", v: "7.5 hours vs. disconnected spreadsheets", c: "hl" },
        ],
        guardrails: [
          { icon: "🧬", name: "Full-Chain Integrity Check", reg: "CAP GEN.20000" },
          { icon: "🔗", name: "Zero-Duplicate-Entry Rule", reg: "ISO 15189 §8.4" },
          { icon: "📈", name: "Process Health Rollup", reg: "ISO 15189 §8.9" },
          { icon: "🧾", name: "Immutable Chain Record", reg: "21 CFR §11.10(e)" },
        ],
      },
    ],
  },
];